import React, { useEffect, useMemo, useState } from "react"
import { useQuery } from "@apollo/client"
import { useTranslation } from "react-i18next"
import ReactApexChart from "react-apexcharts"
import { formatDayMonth } from "../../utils/dateUtils"
import PPUtils from "../../utils/PPUtils"
import { QUERY_AUTOMATCHING_STATS } from "../../utils/matching/matchingQueries"

const PERIOD_OPTIONS = [
    { value: null, key: "all" },
    { value: 7, key: "7d" },
    { value: 30, key: "30d" }
]

const PP_STATS_COLORS = {
    primary: PPUtils.Colors.NeutralStart,
    primaryDark: PPUtils.Colors.NeutralEnd,
    success: PPUtils.Colors.GreenStart,
    warning: PPUtils.Colors.YellowStart,
    orange: PPUtils.Colors.OrangeStart,
    danger: PPUtils.Colors.RedStart,
    muted: PPUtils.Colors.GreyLight,
    ki: "#7b5ea7"
}

const SCORE_BUCKET_COLORS = {
    "90-100": PP_STATS_COLORS.success,
    "80-89": PP_STATS_COLORS.warning,
    "70-79": PP_STATS_COLORS.orange,
    "<70": PP_STATS_COLORS.danger
}

const PRODUCER_BAR_COLORS = [
    PP_STATS_COLORS.success,
    PP_STATS_COLORS.primary,
    PP_STATS_COLORS.orange,
    PP_STATS_COLORS.warning,
    PP_STATS_COLORS.danger
]

function baseChartOptions() {
    return {
        chart: { toolbar: { show: false }, fontFamily: "inherit" },
        grid: { borderColor: "#dee2e6", strokeDashArray: 3 },
        tooltip: { theme: "light" },
        legend: { fontSize: "12px", labels: { colors: "#495057" } }
    }
}

function buildShopColorMap(shops) {
    const map = {}
    ;(shops ?? []).forEach((shop) => {
        map[shop.id] = shop.colorHex || PP_STATS_COLORS.primary
    })
    return map
}

function KpiCard({ label, value, sublabel, accent }) {
    return (
        <div className="pp-matching-stats-kpi">
            <div className="pp-matching-stats-kpi__accent" style={{ background: accent }} />
            <div className="pp-matching-stats-kpi__body">
                <div className="pp-matching-stats-kpi__label">{label}</div>
                <div className="pp-matching-stats-kpi__value">{value}</div>
                {sublabel && <div className="pp-matching-stats-kpi__sub">{sublabel}</div>}
            </div>
        </div>
    )
}

function StatsPanel({ title, children }) {
    return (
        <div className="pp-matching-stats-panel">
            <div className="pp-matching-stats-panel__title">{title}</div>
            {children}
        </div>
    )
}

export default function PPMatchingStatsSidebar({
    shopId,
    displayPosition,
    totalCount,
    statsRefreshKey,
    isNarrowLayout,
    onCollapse
}) {
    const [t] = useTranslation()
    const [periodDays, setPeriodDays] = useState(null)

    const { data, loading, refetch } = useQuery(QUERY_AUTOMATCHING_STATS, {
        variables: { shopId: shopId ?? null, periodDays },
        fetchPolicy: "network-only",
        skip: shopId === null || shopId === undefined
    })

    useEffect(() => {
        if (shopId !== null && shopId !== undefined) {
            refetch()
        }
    }, [statsRefreshKey, shopId, periodDays, refetch])

    const stats = data?.automatchingStats
    const shopColors = useMemo(() => buildShopColorMap(data?.shops), [data?.shops])

    const periodLabel = t(
        `matching.stats.period_${PERIOD_OPTIONS.find((p) => p.value === periodDays)?.key ?? "all"}`
    )

    const queuePercent = totalCount > 0 ? Math.round((displayPosition / totalCount) * 100) : 0

    const shopStats = useMemo(() => {
        const entries = [...(stats?.shopStats ?? [])].filter((s) => s.openCount > 0 || s.approvedInPeriod > 0)
        return entries.sort((a, b) => b.openCount - a.openCount)
    }, [stats?.shopStats])

    const openBarOptions = useMemo(() => {
        const categories = shopStats.map((s) => `${s.name} (${s.lang})`)
        const colors = shopStats.map((s) => shopColors[s.shopId] || PP_STATS_COLORS.primary)
        return {
            ...baseChartOptions(),
            chart: { type: "bar", toolbar: { show: false }, fontFamily: "inherit" },
            plotOptions: {
                bar: { horizontal: true, borderRadius: 4, barHeight: "65%" }
            },
            colors,
            dataLabels: {
                enabled: true,
                style: { fontSize: "11px", fontWeight: 600, colors: ["#fff"] }
            },
            xaxis: { categories, labels: { style: { colors: "#495057", fontSize: "11px" } } },
            yaxis: { labels: { style: { colors: "#495057", fontSize: "11px" } } }
        }
    }, [shopStats, shopColors])

    const openBarSeries = useMemo(
        () => [{ name: t("matching.stats.open_suggestions"), data: shopStats.map((s) => s.openCount) }],
        [shopStats, t]
    )

    const shopActivityOptions = useMemo(() => {
        const categories = shopStats.map((s) => `${s.name} (${s.lang})`)
        return {
            ...baseChartOptions(),
            chart: { type: "bar", stacked: true, toolbar: { show: false }, fontFamily: "inherit" },
            plotOptions: {
                bar: { horizontal: true, borderRadius: 2, barHeight: "65%" }
            },
            colors: [PP_STATS_COLORS.primary, PP_STATS_COLORS.success],
            dataLabels: { enabled: false },
            xaxis: { categories, labels: { style: { colors: "#495057", fontSize: "11px" } } },
            yaxis: { labels: { style: { colors: "#495057", fontSize: "11px" } } },
            legend: { position: "top", fontSize: "12px", labels: { colors: "#495057" } }
        }
    }, [shopStats])

    const shopActivitySeries = useMemo(
        () => [
            { name: t("matching.stats.open_suggestions"), data: shopStats.map((s) => s.openCount) },
            { name: t("matching.stats.approved"), data: shopStats.map((s) => s.approvedInPeriod) }
        ],
        [shopStats, t]
    )

    const dailyLabels = useMemo(
        () =>
            (stats?.dailyActivity ?? []).map((d) => formatDayMonth(d.date)),
        [stats?.dailyActivity]
    )

    const dailyOptions = useMemo(
        () => ({
            ...baseChartOptions(),
            chart: { type: "area", toolbar: { show: false }, fontFamily: "inherit", zoom: { enabled: false } },
            stroke: { curve: "smooth", width: 2.5 },
            fill: {
                type: "gradient",
                gradient: { opacityFrom: 0.45, opacityTo: 0.08 }
            },
            colors: [PP_STATS_COLORS.primary, PP_STATS_COLORS.success],
            dataLabels: { enabled: false },
            xaxis: {
                categories: dailyLabels,
                labels: { rotate: -45, style: { fontSize: "10px", colors: "#495057" } }
            },
            yaxis: { labels: { style: { colors: "#495057", fontSize: "11px" } } },
            legend: { position: "top", fontSize: "12px", labels: { colors: "#495057" } }
        }),
        [dailyLabels]
    )

    const dailySeries = useMemo(
        () => [
            {
                name: t("matching.stats.new_suggestions"),
                data: (stats?.dailyActivity ?? []).map((d) => d.createdCount)
            },
            {
                name: t("matching.stats.approved"),
                data: (stats?.dailyActivity ?? []).map((d) => d.approvedCount)
            }
        ],
        [stats?.dailyActivity, t]
    )

    const scoreBuckets = stats?.scoreBuckets ?? []
    const scoreBucketColors = useMemo(
        () => scoreBuckets.map((b) => SCORE_BUCKET_COLORS[b.label] || PP_STATS_COLORS.muted),
        [scoreBuckets]
    )

    const scoreOptions = useMemo(
        () => ({
            ...baseChartOptions(),
            chart: { type: "bar", toolbar: { show: false }, fontFamily: "inherit" },
            plotOptions: {
                bar: { distributed: true, borderRadius: 6, columnWidth: "58%" }
            },
            colors: scoreBucketColors,
            dataLabels: {
                enabled: true,
                style: { fontSize: "12px", fontWeight: 700, colors: ["#fff"] }
            },
            xaxis: {
                categories: scoreBuckets.map((b) => b.label),
                labels: { style: { colors: "#495057", fontSize: "11px", fontWeight: 600 } }
            },
            yaxis: { labels: { style: { colors: "#495057", fontSize: "11px" } } },
            legend: { show: false }
        }),
        [scoreBuckets, scoreBucketColors]
    )

    const scoreSeries = useMemo(
        () => [{ name: t("matching.stats.open_suggestions"), data: scoreBuckets.map((b) => b.count) }],
        [scoreBuckets, t]
    )

    const kiCount = stats?.matchSourceSplit?.kiCount ?? 0
    const algoCount = stats?.matchSourceSplit?.algorithmCount ?? 0
    const sourceSeries = [kiCount, algoCount]

    const sourceOptions = useMemo(
        () => ({
            ...baseChartOptions(),
            labels: [t("matching.source_ki"), t("matching.source_algorithm")],
            colors: [PP_STATS_COLORS.ki, PP_STATS_COLORS.primary],
            chart: { fontFamily: "inherit" },
            legend: { position: "bottom", fontSize: "12px", labels: { colors: "#495057" } },
            dataLabels: {
                enabled: true,
                style: { fontSize: "12px", fontWeight: 700, colors: ["#fff"] },
                dropShadow: { enabled: false }
            },
            plotOptions: {
                pie: {
                    donut: { size: "62%" },
                    dataLabels: { offset: 0 }
                }
            },
            stroke: { width: 2, colors: ["#fff"] }
        }),
        [t]
    )

    const topProducers = stats?.topProducers ?? []

    const producerBarOptions = useMemo(() => {
        const categories = topProducers.map((p) => p.name)
        const colors = topProducers.map((_, i) => PRODUCER_BAR_COLORS[i % PRODUCER_BAR_COLORS.length])
        return {
            ...baseChartOptions(),
            chart: { type: "bar", toolbar: { show: false }, fontFamily: "inherit" },
            plotOptions: {
                bar: { horizontal: true, distributed: true, borderRadius: 4, barHeight: "62%" }
            },
            colors,
            dataLabels: {
                enabled: true,
                style: { fontSize: "11px", fontWeight: 600, colors: ["#fff"] }
            },
            xaxis: { categories, labels: { style: { colors: "#495057", fontSize: "11px" } } },
            yaxis: { labels: { style: { colors: "#495057", fontSize: "11px" } } },
            legend: { show: false }
        }
    }, [topProducers])

    const producerBarSeries = useMemo(
        () => [{ name: t("matching.stats.open_suggestions"), data: topProducers.map((p) => p.count) }],
        [topProducers, t]
    )

    if (shopId === null || shopId === undefined) {
        return null
    }

    return (
        <aside className="pp-matching-page__stats" key={statsRefreshKey}>
            <div className="pp-matching-stats">
                <div className="pp-matching-stats__header">
                    {isNarrowLayout && (
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm pp-matching-stats__back-btn"
                            onClick={onCollapse}
                            title={t("matching.stats.back_to_matching")}
                        >
                            <i className="fas fa-chevron-left" aria-hidden="true" />
                            <span>{t("matching.stats.back_to_matching")}</span>
                        </button>
                    )}
                    <h4 className="pp-matching-stats__title">{t("matching.stats.title")}</h4>
                    <div className="pp-matching-stats__header-actions">
                        <div className="btn-group btn-group-sm pp-matching-stats__period" role="group">
                            {PERIOD_OPTIONS.map((opt) => (
                                <button
                                    key={opt.key}
                                    type="button"
                                    className={
                                        "btn btn-outline-secondary" +
                                        (periodDays === opt.value ? " active" : "")
                                    }
                                    onClick={() => setPeriodDays(opt.value)}
                                >
                                    {t(`matching.stats.period_${opt.key}`)}
                                </button>
                            ))}
                        </div>
                        {!isNarrowLayout && (
                            <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm pp-matching-stats__collapse-btn"
                                onClick={onCollapse}
                                title={t("matching.stats.hide")}
                                aria-label={t("matching.stats.hide")}
                            >
                                <i className="fas fa-chevron-right" aria-hidden="true" />
                            </button>
                        )}
                    </div>
                </div>

                {loading && !stats ? (
                    <div className="pp-matching-stats__loading text-muted small">{t("matching.stats.loading")}</div>
                ) : (
                    <>
                        <div className="pp-matching-stats-kpi-grid">
                            <KpiCard
                                label={t("matching.stats.open_suggestions")}
                                value={stats?.openCount ?? 0}
                                sublabel={t("matching.stats.open_total_hint", {
                                    total: stats?.openCountTotal ?? 0
                                })}
                                accent={PP_STATS_COLORS.primary}
                            />
                            <KpiCard
                                label={t("matching.stats.approved")}
                                value={stats?.approvedCount ?? 0}
                                sublabel={periodLabel}
                                accent={PP_STATS_COLORS.success}
                            />
                            <KpiCard
                                label={t("matching.stats.avg_score")}
                                value={
                                    stats?.averageOpenScore > 0
                                        ? `${stats.averageOpenScore} %`
                                        : "–"
                                }
                                sublabel={t("matching.stats.avg_score_hint")}
                                accent={PP_STATS_COLORS.orange}
                            />
                            <KpiCard
                                label={t("matching.stats.queue_progress")}
                                value={`${displayPosition} / ${totalCount}`}
                                sublabel={`${queuePercent} %`}
                                accent="#6c757d"
                            />
                        </div>

                        {shopStats.length > 0 && (
                            <StatsPanel title={t("matching.stats.chart_open_by_shop")}>
                                <ReactApexChart
                                    options={openBarOptions}
                                    series={openBarSeries}
                                    type="bar"
                                    height={Math.max(140, shopStats.length * 36)}
                                />
                            </StatsPanel>
                        )}

                        {shopStats.length > 0 && (
                            <StatsPanel title={t("matching.stats.chart_shop_activity")}>
                                <ReactApexChart
                                    options={shopActivityOptions}
                                    series={shopActivitySeries}
                                    type="bar"
                                    height={Math.max(160, shopStats.length * 40)}
                                />
                            </StatsPanel>
                        )}

                        {(stats?.dailyActivity?.length ?? 0) > 0 && (
                            <StatsPanel title={t("matching.stats.chart_daily")}>
                                <ReactApexChart
                                    options={dailyOptions}
                                    series={dailySeries}
                                    type="area"
                                    height={200}
                                />
                            </StatsPanel>
                        )}

                        {scoreBuckets.some((b) => b.count > 0) && (
                            <StatsPanel title={t("matching.stats.chart_score_distribution")}>
                                <ReactApexChart
                                    options={scoreOptions}
                                    series={scoreSeries}
                                    type="bar"
                                    height={180}
                                />
                            </StatsPanel>
                        )}

                        {kiCount + algoCount > 0 && (
                            <StatsPanel title={t("matching.stats.chart_source_split")}>
                                <ReactApexChart
                                    options={sourceOptions}
                                    series={sourceSeries}
                                    type="donut"
                                    height={220}
                                />
                            </StatsPanel>
                        )}

                        {topProducers.length > 0 && (
                            <StatsPanel title={t("matching.stats.top_producers")}>
                                <ReactApexChart
                                    options={producerBarOptions}
                                    series={producerBarSeries}
                                    type="bar"
                                    height={Math.max(130, topProducers.length * 34)}
                                />
                            </StatsPanel>
                        )}
                    </>
                )}
            </div>
        </aside>
    )
}
